'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { ProductLogoMediumDevices } from '@/components/Logo';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { MemberDevice, PortalAssetStatus } from '@/mocks/db/types';
import { MY_MANAGED_PLACE_IDS, MY_PORTAL_PERSON_ID } from '../-shared/-api';
import { ledgerColumn } from '../-shared/-components/ledgerColumn';
import { LedgerContent } from '../-shared/-components/LedgerContent';
import { LedgerDataTable } from '../-shared/-components/LedgerDataTable';
import { type LedgerFilterTab, LedgerFilterTabs } from '../-shared/-components/LedgerFilterTabs';
import { LedgerHeader } from '../-shared/-components/LedgerHeader';
import { AssetStatusBadge, PersonCell, PlaceCell } from '../-shared/-components/ResourceCells';
import { Field, FieldGrid, ResourceDetailCard } from '../-shared/-components/ResourceDetailCard';
import { useMemberDevices, usePortalPersons, usePortalPlaces } from '../-shared/-query';
import { formatDate, toMapById } from '../-shared/-utils';
const DEVICES_PATH = '/individual/portal/lookup/devices';
const LOOKUP_PATH = '/individual/portal/lookup';
const devicesSearchSchema = z.object({
  scope: z.enum(['self', 'managed', 'all']).catch('self'),
  id: z.string().optional()
});
export const Route = createFileRoute('/_individual/individual/portal/lookup/_ledger/devices/')({
  validateSearch: devicesSearchSchema,
  component: DevicesLookupPage
});
function inManagedPlace(device: MemberDevice): boolean {
  return device.installPlaceId != null && MY_MANAGED_PLACE_IDS.includes(device.installPlaceId) || device.storagePlaceId != null && MY_MANAGED_PLACE_IDS.includes(device.storagePlaceId);
}
function DevicesLookupPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.lookupResources'
  });
  const {
    t: ct
  } = useTranslation(defaultNS, {
    keyPrefix: 'common.breadcrumbs'
  });
  const {
    t: th
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.home'
  });
  const {
    scope,
    id
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    data: devices,
    isPending
  } = useMemberDevices();
  const {
    data: persons
  } = usePortalPersons();
  const {
    data: places
  } = usePortalPlaces();
  const personsById = useMemo(() => toMapById(persons), [persons]);
  const placesById = useMemo(() => toMapById(places), [places]);
  const selected = id != null ? devices?.find(d => d.id === id) ?? null : null;
  const rows = useMemo(() => {
    const all = devices ?? [];
    if (scope === 'self') {
      return all.filter(d => d.holderPersonId === MY_PORTAL_PERSON_ID);
    }
    if (scope === 'managed') {
      return all.filter(inManagedPlace);
    }
    return all;
  }, [devices, scope]);
  const statusLabel = (status: PortalAssetStatus) => status === 'assigned' ? t('devices.status.assigned') : t('devices.status.installed');
  const placeName = (placeId: string | null) => placeId != null ? placesById.get(placeId)?.name ?? null : null;
  if (selected != null) {
    return <>
        <LedgerHeader breadcrumbs={[{
        label: ct('home'),
        link: {
          to: '/'
        }
      }, {
        label: th('lookup.title'),
        link: {
          to: LOOKUP_PATH
        }
      }, {
        label: t('devices.title'),
        link: {
          to: DEVICES_PATH,
          search: {
            scope
          }
        }
      }, {
        label: selected.name
      }]} Logo={ProductLogoMediumDevices} title={selected.name} subtitle={selected.assetTag} />
        <LedgerContent>
          <ResourceDetailCard title={t('devices.detail.basicInfo')}>
            <FieldGrid>
              <Field label={t('devices.detail.category')} value={selected.category} />
              <Field label={t('devices.detail.status')} value={<AssetStatusBadge status={selected.status} label={statusLabel(selected.status)} />} />
              <Field label={t('devices.detail.holder')} value={<PersonCell person={selected.holderPersonId != null ? personsById.get(selected.holderPersonId) : null} />} />
              <Field label={t('devices.detail.assetTag')} value={selected.assetTag} />
              <Field label={t('devices.detail.installPlace')} value={<PlaceCell name={placeName(selected.installPlaceId)} />} />
              <Field label={t('devices.detail.storagePlace')} value={<PlaceCell name={placeName(selected.storagePlaceId)} />} />
              <Field label={t('devices.detail.serialNumber')} value={selected.serialNumber} />
              <Field label={t('devices.detail.os')} value={selected.os} />
              <Field label={t('devices.detail.purchasedAt')} value={formatDate(selected.purchasedAt)} />
            </FieldGrid>
          </ResourceDetailCard>
        </LedgerContent>
      </>;
  }
  const columns = [ledgerColumn<MemberDevice>({
    id: 'name',
    header: t('devices.headers.name'),
    cell: d => <Text variant="label">{d.name}</Text>
  }), ledgerColumn<MemberDevice>({
    id: 'category',
    header: t('devices.headers.category'),
    cell: d => d.category
  }), ledgerColumn<MemberDevice>({
    id: 'assetTag',
    header: t('devices.headers.assetTag'),
    cell: d => d.assetTag
  }), ledgerColumn<MemberDevice>({
    id: 'status',
    header: t('devices.headers.status'),
    cell: d => <AssetStatusBadge status={d.status} label={statusLabel(d.status)} />
  }), ledgerColumn<MemberDevice>({
    id: 'holder',
    header: t('devices.headers.holder'),
    cell: d => <PersonCell person={d.holderPersonId != null ? personsById.get(d.holderPersonId) : null} />
  }), ledgerColumn<MemberDevice>({
    id: 'installPlace',
    header: t('devices.headers.installPlace'),
    cell: d => <PlaceCell name={placeName(d.installPlaceId)} />
  }), ledgerColumn<MemberDevice>({
    id: 'storagePlace',
    header: t('devices.headers.storagePlace'),
    cell: d => <PlaceCell name={placeName(d.storagePlaceId)} />
  })];
  const scopeTabs: LedgerFilterTab<'self' | 'managed' | 'all'>[] = [{
    value: 'self',
    label: t('devices.tabs.self')
  }, {
    value: 'managed',
    label: t('devices.tabs.managed')
  }, {
    value: 'all',
    label: t('devices.tabs.all')
  }];
  return <>
      <LedgerHeader breadcrumbs={[{
      label: ct('home'),
      link: {
        to: '/'
      }
    }, {
      label: th('lookup.title'),
      link: {
        to: LOOKUP_PATH
      }
    }, {
      label: t('devices.title')
    }]} Logo={ProductLogoMediumDevices} title={t('devices.title')} />
      <LedgerContent className="h-full">
        <LedgerDataTable<MemberDevice> fillHeight columns={columns} data={rows} isPending={isPending} filterTabs={<LedgerFilterTabs tabs={scopeTabs} value={scope} onSelect={s => void navigate({
        search: prev => ({
          ...prev,
          scope: s
        })
      })} ariaLabel={t('common.filterTabsLabel')} />} getRowId={d => d.id} getSearchText={d => `${d.name} ${d.category} ${d.assetTag}`} searchPlaceholder={t('common.search')} onRowSelect={d => void navigate({
        search: prev => ({
          ...prev,
          id: d.id
        })
      })} emptyContent={<Text variant="p" className="text-neutral-muted-on-fill">
              {t('common.empty')}
            </Text>} />
      </LedgerContent>
    </>;
}